<template>
  <div
    v-if="show"
    class="fixed inset-0 bg-gray-900 bg-opacity-60 overflow-y-hidden h-full w-full flex items-center justify-center text-base z-999"
  >
    <!-- Loading overlay -->
    <div v-if="showLoader" class="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
      <div class="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-yellow-500"></div>
    </div>

    <div
      v-else-if="visit && visit.patient"
      class="bg-white rounded-lg shadow-xl w-11/12 flex flex-col"
      style="height: 88vh;"
    >
      <!-- ── Header ──────────────────────────────────────────────── -->
      <div class="flex items-center justify-between p-4 border-b shrink-0">
        <div class="flex items-center gap-3 flex-wrap flex-1 space-x-16 mr-4">
          <h2 class="text-2xl text-gray-800 font-semibold shrink-0">Procedure / Service</h2>

          <div v-if="procedures.length > 0" class="flex items-center gap-2 flex-wrap">
            <button
              v-for="(proc, idx) in procedures"
              :key="proc.id"
              @click="goToProcedure(idx)"
              class="flex items-center gap-1.5 px-3 py-1 rounded-md text-sm font-semibold transition-colors border"
              :class="idx === currentProcedureIndex
                ? 'bg-custom-yellow text-white border-custom-yellow'
                : completedIndices.includes(idx)
                  ? 'bg-green-500 text-white border-green-500'
                  : 'bg-gray-100 text-gray-600 border-gray-300 hover:bg-gray-200'"
            >
              <span
                class="w-4 h-4 rounded-full flex items-center justify-center font-bold shrink-0 text-sm"
                :class="idx === currentProcedureIndex
                  ? 'bg-white text-custom-yellow'
                  : completedIndices.includes(idx)
                    ? 'bg-white text-green-600'
                    : 'bg-gray-300 text-gray-600'"
              >{{ idx + 1 }}</span>
              <span class="max-w-[120px] truncate">{{ procedureName(proc) }}</span>
              <svg v-if="completedIndices.includes(idx)" class="w-3 h-3 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7"/>
              </svg>
            </button>
          </div>
        </div>

        <button @click="closeModal" class="text-gray-500 hover:text-gray-700 shrink-0" aria-label="Close">
          <svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
          </svg>
        </button>
      </div>

      <!-- ── Body ───────────────────────────────────────────────── -->
      <div class="flex flex-1 overflow-hidden">

        <!-- Left column -->
        <div class="w-2/12 p-4 border-r overflow-y-auto shrink-0">
          <div class="flex items-center mb-4">
            <img src="@/assets/images/avatar.png" alt="Patient" class="w-12 h-12 rounded-full mr-4" />
            <div>
              <h3 class="font-semibold">{{ visit?.patient.FullName }}</h3>
            </div>
          </div>

          <div class="space-y-2 py-2">
            <p class="flex flex-col">
              <span class="font-semibold">Unique Code</span>
              <span>{{ visit?.patient.unique_code }}</span>
            </p>
            <p><span class="font-semibold">Email:</span> {{ visit?.patient.Email || "N/A" }}</p>
            <p><span class="font-semibold">Phone:</span> {{ visit?.patient.PhoneNumber || "N/A" }}</p>
            <p><span class="font-semibold">D.O.B:</span> {{ visit?.patient.Dob }}</p>
            <p><span class="font-semibold">Gender:</span> {{ visit?.patient.Gender }}</p>
            <p><span class="font-semibold">Emergency Contact:</span> {{ visit?.patient?.emergency_contact?.Name || "N/A" }}</p>
            <p><span class="font-semibold">Phone:</span> {{ visit?.patient?.emergency_contact?.PhoneNumber || "N/A" }}</p>

            <div class="mt-4 mb-4 flex justify-center">
              <button
                @click="handleMedicalHistory(visit?.patient?.PatientId)"
                class="px-4 py-1 bg-custom-yellow text-white rounded hover:bg-yellow-600"
              >
                Medical History
              </button>
            </div>
            <h4 class="font-semibold py-2 border-b-2 border-t-2 text-center">Current Vitals</h4>
          </div>

          <div class="grid grid-cols-3 gap-4">
            <div class="flex flex-col items-center justify-center text-center">
              <span class="text-custom-yellow text-2xl">🌡️</span>
              <p class="text-xs">Temperature</p>
              <p class="font-medium text-base">{{ currentVitals.temperature }}°C</p>
            </div>
            <div class="flex flex-col items-center justify-center text-center">
              <span class="text-custom-yellow text-2xl">❤️</span>
              <p class="text-xs">Heart Rate</p>
              <p class="font-medium text-base">{{ currentVitals.heart_rate || "-" }}BPM</p>
            </div>
            <div class="flex flex-col items-center justify-center text-center">
              <span class="dark:custom-yellow text-xl">🫁</span>
              <p class="text-xs">Respiratory</p>
              <p class="font-medium text-base">{{ currentVitals.respiratory_rate || "-" }}</p>
            </div>
            <div class="flex flex-col items-center justify-center text-center">
              <span class="text-custom-yellow text-xl">🩸</span>
              <p class="text-xs">Blood Pressure</p>
              <p class="font-medium text-base">
                {{ currentVitals.blood_pressure_systolic }} / {{ currentVitals.blood_pressure_diastolic }}
              </p>
            </div>
            <div class="flex flex-col items-center justify-center text-center">
              <span class="text-custom-yellow text-2xl">⚖️</span>
              <p class="text-xs">Weight</p>
              <p class="font-medium text-base">{{ currentVitals.weight }} KG</p>
            </div>
            <div class="flex flex-col items-center justify-center text-center">
              <span class="text-custom-yellow text-2xl">📏</span>
              <p class="text-xs">Height</p>
              <p class="font-medium text-base">{{ currentVitals.height }} cm</p>
            </div>
          </div>

          <div
            v-if="currentProcedure"
            class="mt-4 p-3 rounded border"
            :class="completedIndices.includes(currentProcedureIndex)
              ? 'bg-green-50 border-green-200'
              : 'bg-blue-50 border-blue-200'"
          >
            <p
              class="text-xs font-semibold mb-1"
              :class="completedIndices.includes(currentProcedureIndex) ? 'text-green-700' : 'text-blue-700'"
            >
              {{ completedIndices.includes(currentProcedureIndex) ? '✓ Completed Procedure' : 'Current Procedure' }}
            </p>
            <p class="text-sm font-medium text-gray-800">{{ procedureName(currentProcedure) }}</p>
            <p v-if="currentProcedure.notes" class="text-xs text-gray-500 mt-1">
              Notes: {{ currentProcedure.notes }}
            </p>
          </div>
        </div>

        <!-- Middle column: tab menu -->
        <div class="w-2/12 p-4 border-r overflow-y-auto shrink-0">
          <ul class="mt-1">
            <li v-for="item in menuItems" :key="item.id" class="px-4 py-1">
              <a
                href="#"
                :class="[
                  'block px-4 py-1 rounded-md',
                  currentView === item.component
                    ? 'bg-custom-blue border-b-2 border-custom-yellow text-white font-semibold'
                    : 'text-gray-500 hover:bg-gray-100',
                ]"
                @click.prevent="setCurrentView(item.component)"
              >
                {{ item.title }}
              </a>
            </li>
          </ul>
        </div>

        <!-- Right column -->
        <div class="flex-1 p-6 overflow-y-auto">
          <div v-if="errors.length" class="flex mt-4 p-4 bg-red-300 mb-4">
            <ul>
              <li v-for="(error, index) in errors" :key="index">{{ error }}</li>
            </ul>
          </div>

          <keep-alive>
            <component
              :is="currentView"
              :key="`proc-${currentProcedure?.id}-${currentView}`"
              :state="state"
              :visitId="visitId"
              :ref="comp => { if (comp) componentRefs[currentView] = comp; }"
              :optional="true"
              :existingPhysicalExam="existingData.physical_exam"
              :existingComplaint="existingData.complaint"
              :existingFindings="existingData.findings"
              :existingPreliminaryDiagnosis="existingData.preliminary_diagnosis"
              :existingFinalDiagnosis="existingData.final_diagnosis"
              :existingSocialHistories="existingData.social_histories"
              :existingFamilyHistories="existingData.family_histories"
              :existingLabTests="sharedLabTests"
              :existingRadiologyTests="sharedRadiologyTests"
              :radiologyTests="sharedRadiologyTests"
              :existingPrescriptions="sharedPrescriptions"
              :existingProcedures="sharedProcedures"
              :mode="'procedure'"
              :patientId="visit?.patient?.PatientId"
              :patientGender="patientGender"
              :drug_allergies="visit?.patient?.drug_allergies"
              :food_allergies="visit?.patient?.food_allergies"
              :drug_histories="visit?.patient?.drug_histories"
              :medical_histories="visit?.patient?.medical_histories"
              :family_histories="visit?.patient?.family_histories"
              :triage_medication="visit?.patient?.triage_medication"
              :current_medication="visit?.patient?.current_medication"
              :existingExternalPrescription="existingExternalPrescription"
              :visitDetails="prescriptionVisitDetails"
              :labTests="sharedLabTests"
              @results-published="fetchVisitDetails"
            />
          </keep-alive>
        </div>
      </div>

      <!-- ── Footer ─────────────────────────────── -->
      <div class="flex justify-between items-center px-6 py-3 border-t bg-white shrink-0">
        <div class="text-sm text-gray-500">
          <span v-if="currentProcedure">
            <strong>{{ procedureName(currentProcedure) }}</strong>
            <span
              v-if="completedIndices.includes(currentProcedureIndex)"
              class="ml-2 text-green-600 font-medium"
            >✓ Saved</span>
          </span>
        </div>
        <div class="flex space-x-2">
          <button @click="handlePreviousTab" class="px-8 py-1 bg-gray-200 text-gray-800 rounded">Back</button>

          <button
            v-if="currentView !== 'PrescriptionTab'"
            @click="handleNextTab"
            class="px-8 py-1 bg-custom-yellow text-white rounded"
          >
            Next
          </button>

          <button
            v-if="currentView === 'PrescriptionTab'"
            :disabled="saving"
            @click="saveProcedureAndAdvance"
            class="px-8 py-1 bg-custom-blue text-white rounded disabled:opacity-50"
          >
            <span v-if="saving">Saving...</span>
            <span v-else-if="allOtherProceduresCompleted">Save & Close</span>
            <span v-else>Save & Next Procedure →</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Empty / error state -->
    <div v-else class="bg-white rounded-lg shadow-xl w-11/12 p-8 text-center">
      <p class="text-gray-600 mb-4">Unable to load visit details</p>
      <button @click="closeModal" class="px-6 py-2 bg-custom-yellow text-white rounded hover:bg-yellow-600">Close</button>
    </div>
  </div>
</template>

<script>
import PhysicalExam           from "@/views/tabs/consultation_tabs/PhysicalExam.vue";
import PresentingComplaint    from "@/views/tabs/consultation_tabs/PresentingComplaint.vue";
import InvestigationProcedure from "@/views/tabs/consultation_tabs/InvestigationProcedure.vue";
import ProcedureResults       from "@/views/tabs/consultation_tabs/ProcedureResults.vue";
import EncounterSummaryTab    from "@/views/tabs/consultation_tabs/EncounterSummaryTab.vue";
import PatientDocumentsTab    from "@/views/tabs/consultation_tabs/PatientDocumentsTab.vue";
import DiagnosisTab           from "@/views/tabs/consultation_tabs/DiagnosisTab.vue";
import PrescriptionTab        from "@/views/tabs/consultation_tabs/PrescriptionTab.vue";
import ExternalLabResultsTab  from "@/views/tabs/consultation_tabs/ExternalLabResultsTab.vue";
import ExternalRadiologyResultsTab from "@/views/tabs/consultation_tabs/ExternalRadiologyResultsTab.vue";
import apiClient              from "@/services/api";
import { buildProcedureMenuItems } from "@/config/encounterTabs.js";

const PROCEDURE_DONE_STATUS = "ST-005";

export default {
  name: "ProcedurePatientQueueModal",

  components: {
    PhysicalExam,
    PresentingComplaint,
    InvestigationProcedure,
    ProcedureResults,
    EncounterSummaryTab,
    PatientDocumentsTab,
    DiagnosisTab,
    PrescriptionTab,
    ExternalLabResultsTab,
    ExternalRadiologyResultsTab,
  },

  props: {
    show:    { type: Boolean, required: true },
    visitId: { type: String,  required: true },
    state:   { type: String,  required: true },
  },

  data() {
    return {
      showLoader: false,
      saving:     false,
      visit:      null,
      patientGender: "",

      procedures:            [],
      currentProcedureIndex: 0,
      completedIndices:      [],

      existingData: {
        physical_exam:         {},
        complaint:             {},
        findings:              "",
        preliminary_diagnosis: [],
        final_diagnosis:       [],
        social_histories:      [],
        family_histories:      [],   // ← added
      },

      sharedLabTests:               [],
      sharedRadiologyTests:         [],
      sharedPrescriptions:          [],
      sharedProcedures:             [],
      existingExternalPrescription: null,

      currentVitals: {
        temperature: "", heart_rate: "", respiratory_rate: "",
        blood_pressure_systolic: "", blood_pressure_diastolic: "",
        weight: "", height: "",
      },

      currentView: "PresentingComplaint",
      menuItems: buildProcedureMenuItems(),

      componentRefs: {},
      errors: [],
      facilityLogoUrl: null,
    };
  },

  computed: {
    currentProcedure() {
      return this.procedures[this.currentProcedureIndex] || null;
    },
    isLastProcedure() {
      return this.currentProcedureIndex >= this.procedures.length - 1;
    },
    allOtherProceduresCompleted() {
      return this.procedures.every((_, idx) =>
        idx === this.currentProcedureIndex || this.completedIndices.includes(idx)
      );
    },
    prescriptionVisitDetails() {
      return {
        visitDate:  this.visit?.VisitStartTime || null,
        prescriber: "",
        patient:    this.visit?.patient || null,
        facility: {
          FacilityName: this.visit?.patient?.facility_name || "",
          NearestTown:  "",
          County:       "",
          logo_url:     this.facilityLogoUrl,
        },
      };
    },
  },

  watch: {
    visitId: {
      immediate: true,
      handler(val) {
        if (val) this.fetchVisitDetails();
      },
    },
  },

  methods: {
    procedureName(proc) {
      return proc?.facility_service?.name
          || proc?.facility_service_name
          || proc?.description
          || `Procedure ${proc?.item_id?.slice(0, 8) || ""}`;
    },

    async fetchVisitDetails() {
      try {
        this.showLoader = true;
        const response  = await apiClient.getConsultationVisitDetails(this.visitId);

        if (!response.success) {
          this.$toast.error("Unable to fetch visit information.");
          return;
        }

        this.visit      = response.data;
        this.procedures = (this.visit.procedures || []).filter(p => p.id);

        this.completedIndices = this.procedures.reduce((acc, proc, idx) => {
          if (proc.status?.code === PROCEDURE_DONE_STATUS) acc.push(idx);
          return acc;
        }, []);

        const firstPending = this.procedures.findIndex(
          (_, idx) => !this.completedIndices.includes(idx)
        );
        const startIndex = firstPending >= 0 ? firstPending : 0;

        if (this.visit.patient?.latest_triage_vital) {
          this.currentVitals = { ...this.currentVitals, ...this.visit.patient.latest_triage_vital };
        }
        this.patientGender = this.visit.patient?.Gender || "";

        this.sharedLabTests               = this.visit.lab_tests       || [];
        this.sharedRadiologyTests         = this.visit.radiology_tests || [];
        this.sharedPrescriptions          = this.visit.prescriptions   || [];
        this.sharedProcedures             = this.visit.procedures      || [];
        this.existingExternalPrescription = this.visit.external_prescription || null;

        this.currentProcedureIndex = startIndex;
        this.loadExistingDataFor(startIndex);

        if (this.state !== 'create') {
          this.currentView = 'EncounterSummaryTab';
        }

        await this.fetchFacilityLogo();

      } catch (error) {
        console.error("Error fetching visit:", error);
        this.$toast.error("Failed to fetch visit, please check your internet connection.");
      } finally {
        this.showLoader = false;
      }
    },

    async fetchFacilityLogo() {
      try {
        const user = JSON.parse(localStorage.getItem("user") || "{}");
        if (!user?.FacilityId) return;
        const response = await apiClient.fetchFacility(user.FacilityId);
        if (response.success && response.data?.logo_url) {
          this.facilityLogoUrl = response.data.logo_url;
        }
      } catch {
        // Non-critical
      }
    },

    loadExistingDataFor(index) {
      const proc   = this.procedures[index];
      const result = proc?.procedure_result;

      this.existingData = {
        physical_exam: result ? {
          general:              result.general              || "",
          respiratory_system:   result.respiratory_system   || "",
          cadiovascular_system: result.cadiovascular_system || "",
          abdomen:              result.abdomen              || "",
          genitourinary:        result.genitourinary        || "",
          ent:                  result.ent                  || "",
          skin:                 result.skin                 || "",
          musculoskeletal:      result.musculoskeletal      || "",
          cns:                  result.cns                  || "",
        } : {},
        complaint: result ? {
          chief_complaint:    result.chief_complaint    || "",
          duration:           result.duration           || "",
          duration_type:      result.duration_type      || "",
          history_of_illness: result.history_of_illness || "",
        } : {},
        findings:              result?.findings || "",
        preliminary_diagnosis: [],
        final_diagnosis:       [],
        social_histories:      [],
        family_histories:      [],   // ← added
      };

      this.componentRefs = {};
      this.errors        = [];
    },

    setCurrentView(component) {
      this.currentView = component;
    },

    handleNextTab() {
      this.errors = [];
      const idx = this.menuItems.findIndex(m => m.component === this.currentView);
      if (idx < this.menuItems.length - 1) {
        this.currentView = this.menuItems[idx + 1].component;
      }
    },

    handlePreviousTab() {
      const idx = this.menuItems.findIndex(m => m.component === this.currentView);
      if (idx > 0) {
        this.currentView = this.menuItems[idx - 1].component;
      }
    },

    goToProcedure(index) {
      if (index === this.currentProcedureIndex) return;
      this.currentProcedureIndex = index;
      this.loadExistingDataFor(index);
    },

    getPhysicalExamPayload() {
      return this.componentRefs["PhysicalExam"]?.submitPhysicalExam?.() || {};
    },
    getPresentingComplaintPayload() {
      return this.componentRefs["PresentingComplaint"]?.submitPresentingComplaint?.() || {};
    },
    getRequestsPayload() {
      return this.componentRefs["InvestigationProcedure"]?.submitAllRequests?.() || {};
    },
    getDiagnosisPayload() {
      return this.componentRefs["DiagnosisTab"]?.submitDiagnosis?.() || {};
    },
    getPrescriptionPayload() {
      return this.componentRefs["PrescriptionTab"]?.submitPrescriptions?.() || [];
    },

    async saveProcedureAndAdvance() {
      this.errors = [];
      this.saving = true;

      const savedIndex = this.currentProcedureIndex;

      try {
        const physicalExam     = this.getPhysicalExamPayload();
        const complaintPayload = this.getPresentingComplaintPayload();
        const requestsPayload  = this.getRequestsPayload();
        const diagnosisPayload = this.getDiagnosisPayload();
        const prescriptions    = this.getPrescriptionPayload();

        const diagnosis = [];
        (complaintPayload?.diagnosis || []).forEach(d =>
          diagnosis.push({ diagnosis_id: d.id, diagnosis_type: 1 })
        );
        (diagnosisPayload?.diagnosis || []).forEach(d =>
          diagnosis.push({ diagnosis_id: d.id, diagnosis_type: 2 })
        );

        const user = JSON.parse(localStorage.getItem("user"))?.UserId;

        const complaint = {
          chief_complaint:    complaintPayload?.chief_complaint    || null,
          duration:           complaintPayload?.duration           || null,
          duration_type:      complaintPayload?.duration_type      || null,
          history_of_illness: complaintPayload?.history_of_illness || null,
        };
        const familyHistory = (complaintPayload?.family_history || []).map(f => ({ name: f }));
        const findings      = diagnosisPayload?.findings || "";
        const lab           = requestsPayload?.labRequests       || [];
        const radiology     = requestsPayload?.radiologyRequests || [];
        const procedure     = requestsPayload?.procedureRequests || [];

        // Visits booked with only lab/radiology (no PatientVisitProcedure record at
        // all) have nothing for updateProcedureResult to attach to — it requires a
        // procedure_id. Route those through the same consultation-write endpoint the
        // Consultation modal uses instead, keyed off the visit directly.
        let response;
        if (this.procedures.length === 0) {
          const facilityId = JSON.parse(localStorage.getItem("user"))?.FacilityId;
          response = await apiClient.savePatientConsultation({
            close:          false,
            visit_id:       this.visitId,
            patient_id:     this.visit?.patient?.PatientId,
            userId:         user,
            facilityId,
            physical_exam:  physicalExam,
            complaint,
            family_history: familyHistory,
            findings,
            diagnosis,
            lab,
            radiology,
            procedure,
            prescription:   prescriptions,
          });

          if (response.success) {
            this.$toast.success("Consultation saved.");
            this.$emit("submit", this.visit?.patient?.PatientId);
            this.$emit("close");
            return;
          }
          this.$toast.error("Failed to save.");
          return;
        }

        const payload = {
          procedure_id:   this.currentProcedure?.id,
          visit_id:       this.visitId,
          patient_id:     this.visit?.patient?.PatientId,
          userId:         user,
          physical_exam:  physicalExam,
          complaint,
          family_history: familyHistory,
          findings,
          diagnosis,
          lab,
          radiology,
          procedure,
          prescription:   prescriptions,
        };

        response = await apiClient.updateProcedureResult(payload);

        if (response.success) {
          this.saveAndPatchLocal(savedIndex, physicalExam, complaintPayload, diagnosisPayload);

          if (!this.completedIndices.includes(savedIndex)) {
            this.completedIndices.push(savedIndex);
          }

          const allDone = this.procedures.every((_, idx) =>
            this.completedIndices.includes(idx)
          );

          if (allDone) {
            this.$toast.success("All procedures for this visit have been saved.");
            this.$emit("submit", this.visit?.patient?.PatientId);
            this.$emit("close");
            return;
          }

          const nextPending = this.procedures.findIndex(
            (_, idx) => !this.completedIndices.includes(idx)
          );

          if (nextPending >= 0) {
            this.$toast.success(
              `Procedure ${savedIndex + 1} saved. Moving to next pending procedure.`
            );
            this.currentProcedureIndex = nextPending;
            this.loadExistingDataFor(nextPending);
          }
        } else {
          this.$toast.error("Failed to save procedure.");
        }
      } catch (error) {
        console.error("Error saving procedure:", error);
        this.$toast.error("Failed to save procedure.");
      } finally {
        this.saving = false;
      }
    },

    saveAndPatchLocal(index, physicalExam, complaintPayload, diagnosisPayload) {
      const proc = this.procedures[index];
      if (!proc) return;

      const existing = proc.procedure_result || {};

      this.procedures[index] = {
        ...proc,
        status: { code: PROCEDURE_DONE_STATUS },
        procedure_result: {
          ...existing,
          general:              physicalExam?.general              || existing.general,
          respiratory_system:   physicalExam?.respiratory_system   || existing.respiratory_system,
          cadiovascular_system: physicalExam?.cadiovascular_system || existing.cadiovascular_system,
          abdomen:              physicalExam?.abdomen              || existing.abdomen,
          genitourinary:        physicalExam?.genitourinary        || existing.genitourinary,
          ent:                  physicalExam?.ent                  || existing.ent,
          skin:                 physicalExam?.skin                 || existing.skin,
          musculoskeletal:      physicalExam?.musculoskeletal      || existing.musculoskeletal,
          cns:                  physicalExam?.cns                  || existing.cns,
          chief_complaint:      complaintPayload?.chief_complaint    || existing.chief_complaint,
          duration:             complaintPayload?.duration           || existing.duration,
          duration_type:        complaintPayload?.duration_type      || existing.duration_type,
          history_of_illness:   complaintPayload?.history_of_illness || existing.history_of_illness,
          findings:             diagnosisPayload?.findings           || existing.findings,
        },
      };
    },

    closeModal() {
      this.$emit("close");
    },

    handleMedicalHistory(patientId) {
      const href = this.$router.resolve({
        name:   "ConsultationMedicalHistoryPage",
        params: { patient_id: patientId },
      }).href;
      window.open(href, "_blank");
    },
  },
};
</script>